import { AvailabilityRangeInput, BookingWindow } from "@/api/types";

/**
 * Clip an availability window so it never covers past time: returns the window unchanged
 * when it's fully in the future, advanced to the first slot boundary that's still bookable
 * when it straddles "now", or {@code null} when nothing bookable remains (fully past, or no
 * full slot left today). Slot boundaries are measured from the window's own start, so the
 * kept slots line up with what the server will offer. Pure — {@code nowMs} is passed in.
 */
export function clipWindowToFuture(
  w: BookingWindow,
  slotMinutes: number,
  nowMs: number,
): BookingWindow | null {
  const startMs = Date.parse(w.start);
  const endMs = Date.parse(w.end);
  if (startMs >= nowMs) return w; // fully future — unchanged
  const stepMs = Math.max(1, slotMinutes) * 60_000;
  const k = Math.ceil((nowMs - startMs) / stepMs);
  const clipped = startMs + k * stepMs;
  if (clipped + stepMs > endMs) return null; // no full future slot remains
  return { start: new Date(clipped).toISOString(), end: w.end };
}

/**
 * Merge genuinely OVERLAPPING windows and drop exact duplicates. Overlap is
 * strict ({@code start < prev.end}) so back-to-back blocks that only touch —
 * e.g. the individual slots a range is split into — stay separate. This is the
 * guard that keeps the set free of duplicates and true overlaps.
 */
export function normalizeWindows(windows: BookingWindow[]): BookingWindow[] {
  const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start));
  const out: BookingWindow[] = [];
  for (const w of sorted) {
    const last = out[out.length - 1];
    if (last && w.start < last.end) {
      if (w.end > last.end) last.end = w.end; // extend the run
    } else {
      out.push({ ...w });
    }
  }
  return out;
}

/** Merge overlapping AND touching windows into contiguous ranges (un-split). */
export function mergeAdjacent(windows: BookingWindow[]): BookingWindow[] {
  const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start));
  const out: BookingWindow[] = [];
  for (const w of sorted) {
    const last = out[out.length - 1];
    if (last && w.start <= last.end) {
      if (w.end > last.end) last.end = w.end;
    } else {
      out.push({ ...w });
    }
  }
  return out;
}

/** A busy span (ms since epoch) to carve out of availability — a confirmed meeting, a Google event, an existing booking. */
export type BusyInterval = { start: number; end: number };

/**
 * Carve {@code busy} intervals out of each window, splitting a window into the free pieces
 * around any overlap and dropping pieces with no time left. A window fully covered by busy
 * time yields nothing — so you can't offer a booking slot where you're already occupied.
 * Pure; matches the server's windows−busy preview so what's drawn is what invitees see.
 */
export function subtractBusy(windows: BookingWindow[], busy: BusyInterval[]): BookingWindow[] {
  if (busy.length === 0) return windows;
  const out: BookingWindow[] = [];
  for (const w of windows) {
    let segments: BusyInterval[] = [{ start: Date.parse(w.start), end: Date.parse(w.end) }];
    for (const b of busy) {
      const next: BusyInterval[] = [];
      for (const s of segments) {
        if (b.end <= s.start || b.start >= s.end) {
          next.push(s); // no overlap — keep whole
          continue;
        }
        if (b.start > s.start) next.push({ start: s.start, end: Math.min(b.start, s.end) });
        if (b.end < s.end) next.push({ start: Math.max(b.end, s.start), end: s.end });
        // otherwise this segment is fully covered → dropped
      }
      segments = next;
    }
    for (const s of segments) {
      if (s.end > s.start) out.push({ start: new Date(s.start).toISOString(), end: new Date(s.end).toISOString() });
    }
  }
  return out;
}

/** True if the window overlaps any busy interval at all — used to warn when a drawn block will lose slots. */
export function overlapsBusy(w: BookingWindow, busy: BusyInterval[]): boolean {
  const s = Date.parse(w.start);
  const e = Date.parse(w.end);
  return busy.some((b) => b.start < e && b.end > s);
}

/** Crystalise ranges into back-to-back slots of {@code minutes} each (dropping any tail short of a full slot). */
export function splitIntoSlots(windows: BookingWindow[], minutes: number): BookingWindow[] {
  const step = minutes * 60_000;
  const out: BookingWindow[] = [];
  for (const w of windows) {
    let s = new Date(w.start).getTime();
    const end = new Date(w.end).getTime();
    while (s + step <= end) {
      out.push({ start: new Date(s).toISOString(), end: new Date(s + step).toISOString() });
      s += step;
    }
  }
  return out.sort((a, b) => a.start.localeCompare(b.start));
}

/**
 * A drag that spans several days becomes one block per day with the same
 * time-of-day band ("open 09:00–11:00 Mon–Wed"), which is what dragging sideways
 * across the week intends. A single-day selection returns just that block. All-day
 * selections (month view) can't form a band, so they return nothing.
 */
export function splitAcrossDays(start: Date, end: Date, allDay: boolean): BookingWindow[] {
  if (allDay) return [];
  const startDay = atMidnight(start);
  const endDay = atMidnight(end);
  const spanDays = Math.round((endDay.getTime() - startDay.getTime()) / 86_400_000);
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes();

  // Single day, or an end time that can't form a per-day band → the raw block.
  if (spanDays <= 0 || endMin <= startMin) {
    return [{ start: start.toISOString(), end: end.toISOString() }];
  }
  const out: BookingWindow[] = [];
  for (let i = 0; i <= spanDays; i++) {
    const day = new Date(startDay);
    day.setDate(day.getDate() + i);
    const s = new Date(day);
    s.setMinutes(startMin);
    const e = new Date(day);
    e.setMinutes(endMin);
    out.push({ start: s.toISOString(), end: e.toISOString() });
  }
  return out;
}

/**
 * A stable colour per calendar day, so all blocks on one day share a shade.
 * Deliberately avoids emerald green + slate — those are the "Show my calendar"
 * meeting / Google overlay colours, and availability must not be mistaken for them.
 */
const DAY_COLORS = [
  "#a5b4fc", // indigo-300
  "#c4b5fd", // violet-300
  "#f0abfc", // fuchsia-300
  "#93c5fd", // blue-300
  "#fdba74", // orange-300
  "#67e8f9", // cyan-300
  "#fda4af", // rose-300
  "#f9a8d4", // pink-300
];

export function colorForDay(iso: string): string {
  // One hue per weekday (Sun..Sat) so a weekday reads as a single colour across
  // weeks — the calendar blocks and the availability canvas stay in sync.
  const dow = new Date(iso).getDay();
  return DAY_COLORS[dow % DAY_COLORS.length];
}

function atMidnight(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/** Lowercase weekday name (as availability-pattern ranges store them) → JS Date.getDay() (Sun=0). */
const DOW: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m || 0);
}

function windowAt(day: Date, startMin: number, endMin: number): BookingWindow {
  const s = new Date(day);
  s.setHours(0, startMin, 0, 0);
  const e = new Date(day);
  e.setHours(0, endMin, 0, 0);
  return { start: s.toISOString(), end: e.toISOString() };
}

/**
 * Expand an availability pattern's recurring weekly ranges into concrete dated windows across
 * [from, to) — one per matching weekday, at the range's local times. When {@code split}
 * is on, each range is broken into back-to-back {@code slotMinutes} segments (the exact
 * slots offered); otherwise the whole range is one window. Capped at {@code max} so an
 * open-ended horizon can't produce an unbounded list. Shared by the calendar bands and
 * the availability canvas so both show the same slots the booking link offers.
 */
export function expandRangesToWindows(
  seedRanges: AvailabilityRangeInput[],
  from: Date,
  to: Date,
  split: boolean,
  slotMinutes: number,
  max = 500,
): BookingWindow[] {
  const out: BookingWindow[] = [];
  const day = atMidnight(from);
  const stop = new Date(to);
  const step = split && slotMinutes > 0 ? slotMinutes : 0;
  while (day < stop && out.length < max) {
    const dow = day.getDay();
    for (const r of seedRanges) {
      if (!r.days.some((name) => DOW[name.toLowerCase()] === dow)) continue;
      const startMin = hhmmToMinutes(r.earliest);
      const endMin = hhmmToMinutes(r.latest);
      if (step > 0) {
        for (let m = startMin; m + step <= endMin && out.length < max; m += step) {
          out.push(windowAt(day, m, m + step));
        }
      } else if (out.length < max) {
        out.push(windowAt(day, startMin, endMin));
      }
    }
    day.setDate(day.getDate() + 1);
  }
  return out;
}
